import React from 'react';
import { Text } from '@chatsdet/ui';
import { SectionLabel } from '../../cards/components/SectionLabel';
import {
  usePageSkillsAnalysis,
  type PageSkillsAnalysisGroup,
} from '../hooks/useProductCardMetrics';
import { useAuth } from '../../../contexts/AuthContext';

const windows = [7, 30, 90] as const;

const rate = (success: number, total: number) =>
  total === 0 ? '—' : `${Math.round((100 * success) / total)}%`;

function groupByOrganization(groups: PageSkillsAnalysisGroup[]) {
  const result = new Map<
    string,
    { name: string; presented: number; reads: number; upserts: number }
  >();
  for (const group of groups) {
    const current = result.get(group.orgId) ?? {
      name: group.orgName,
      presented: 0,
      reads: 0,
      upserts: 0,
    };
    current.presented += group.presented;
    current.reads += group.reads;
    current.upserts += group.upserts;
    result.set(group.orgId, current);
  }
  return [...result.values()].sort((a, b) => b.reads - a.reads);
}

export function ProductItemPageSkillsAnalysisSection({ productItemId }: { productItemId: string }) {
  const { user } = useAuth();
  const [windowDays, setWindowDays] = React.useState<(typeof windows)[number]>(30);
  const query = usePageSkillsAnalysis(productItemId, windowDays, user?.isSuperadmin === true);
  const analysis = query.data;

  if (user?.isSuperadmin !== true || (analysis && !analysis.applicable)) return null;

  const groups = analysis?.groups ?? [];
  const organizations = groupByOrganization(groups);

  return (
    <section className="space-y-3 border-t border-border pt-6">
      <div className="flex items-center justify-between gap-3">
        <SectionLabel>Page Skills treatment-only descriptive analysis</SectionLabel>
        <select
          aria-label="Page Skills analysis window"
          className="rounded border border-border bg-background px-2 py-1 text-xs"
          value={windowDays}
          onChange={(event) =>
            setWindowDays(Number(event.target.value) as (typeof windows)[number])
          }
        >
          {windows.map((days) => (
            <option key={days} value={days}>
              {days} days
            </option>
          ))}
        </select>
      </div>
      <Text size="xs" className="text-muted-foreground">
        These are descriptive associations, not causal helpfulness verdicts. Page identity is
        pattern-derived; unmatched customer paths are never shown.
      </Text>
      {query.isLoading ? (
        <Text size="xs" className="text-muted-foreground">
          Loading analysis…
        </Text>
      ) : null}
      {query.isError ? (
        <Text size="xs" className="text-danger">
          Could not load Page Skills analysis.
        </Text>
      ) : null}
      {!query.isLoading && !query.isError && groups.length === 0 ? (
        <Text size="xs" className="text-muted-foreground">
          No instrumented treatment events in this window yet.
        </Text>
      ) : null}
      {organizations.length > 0 ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {organizations.map((org) => (
            <div key={org.name} className="rounded border border-border/70 p-2 text-xs">
              <div className="font-medium">{org.name}</div>
              <div className="mt-1 text-muted-foreground">
                {org.reads}/{org.presented} reads · {org.upserts} upserts
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {groups.length > 0 ? (
        <div className="overflow-x-auto rounded border border-border/70">
          <table className="w-full text-left text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="p-2">Org / page</th>
                <th className="p-2">Version</th>
                <th className="p-2">Read / shown</th>
                <th className="p-2">Writes</th>
                <th className="p-2">Step</th>
                <th className="p-2">Terminal</th>
              </tr>
            </thead>
            <tbody>
              {groups.slice(0, 100).map((group) => (
                <tr
                  key={`${group.orgId}:${group.propertyId}:${group.pageIdentity}:${group.skillVersion ?? 'legacy'}`}
                  className="border-t border-border/60"
                >
                  <td className="max-w-56 p-2">
                    <div>{group.orgName}</div>
                    <code className="break-all text-[11px] text-muted-foreground">
                      {group.pathPattern ?? group.pageIdentity}
                    </code>
                  </td>
                  <td className="p-2">
                    {group.skillVersion === null ? 'Legacy' : `v${group.skillVersion}`}
                  </td>
                  <td className="p-2">
                    {group.reads} / {group.presented}
                  </td>
                  <td className="p-2">{group.upserts}</td>
                  <td className="p-2">
                    {rate(group.stepSuccessCount, group.stepOutcomeCount)}{' '}
                    <span className="text-muted-foreground">({group.stepOutcomeCount})</span>
                  </td>
                  <td className="p-2">
                    {rate(group.terminalSuccessCount, group.terminalOutcomeCount)}{' '}
                    <span className="text-muted-foreground">({group.terminalOutcomeCount})</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
